/**
 * Navigate to a path and wait for a DOM element to appear and settle.
 * Uses MutationObserver (event-driven) with a settle delay to confirm
 * the element persists after React finishes rendering.
 *
 * @param {Function} navigate - React Router navigate function
 * @param {string} path - Route path to navigate to
 * @param {string} selector - CSS selector for the target element
 * @param {Object} [options]
 * @param {number} [options.timeout=8000] - Max wait time in ms
 * @param {AbortSignal} [options.signal] - AbortController signal for cancellation
 * @returns {Promise<Element>} The found DOM element
 */
export function navigateAndWaitForElement(navigate, path, selector, options = {}) {
  const { timeout = 8000, signal } = options;

  return new Promise((resolve, reject) => {
    if (signal?.aborted) {
      reject(new DOMException('Aborted', 'AbortError'));
      return;
    }

    navigate(path);

    // Check if element already exists (same-page step)
    const existing = document.querySelector(selector);
    if (existing) {
      resolve(existing);
      return;
    }

    let settled = false;
    let settleTimerId = null;

    const cleanup = () => {
      settled = true;
      observer.disconnect();
      clearTimeout(timeoutId);
      if (settleTimerId) clearTimeout(settleTimerId);
    };

    const timeoutId = setTimeout(() => {
      cleanup();
      reject(new Error(`Element ${selector} not found within ${timeout}ms`));
    }, timeout);

    /**
     * When the element is first found, wait 150ms for React to finish
     * any remaining state transitions, then re-query to confirm it's
     * still in the DOM before resolving.
     */
    const scheduleSettle = () => {
      if (settleTimerId) clearTimeout(settleTimerId);
      settleTimerId = setTimeout(() => {
        if (settled) return;
        const el = document.querySelector(selector);
        if (el) {
          cleanup();
          requestAnimationFrame(() => resolve(el));
        }
        // If element disappeared during settle, keep observing
      }, 150);
    };

    const observer = new MutationObserver(() => {
      if (settled) return;
      const el = document.querySelector(selector);
      if (el) {
        scheduleSettle();
      }
    });

    observer.observe(document.body, { childList: true, subtree: true });

    if (signal) {
      signal.addEventListener('abort', () => {
        cleanup();
        reject(new DOMException('Aborted', 'AbortError'));
      });
    }
  });
}
